import { AxiosError, AxiosResponse } from "axios";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { api } from "../services/api";
import { useLogged } from "./userLogin";

interface IProps {
  children: ReactNode;
}

export interface ITemplateCivilType {
  id?: string;
  title: string;
  description: string;
  user_id?: string;
  company_id?: string;
}

interface ITemplateCivilTypeContext {
  templateTypes: ITemplateCivilType[];
  loading: boolean;
  error: any | null;
  total?: number;
  totalPages: number;
  isNewModalVisible: boolean;
  handleModalVisible: (visible: boolean) => void;
  createTemplateType: (templateType: ITemplateCivilType) => Promise<any>;
  updateTemplateType: (id: string, templateType: ITemplateCivilType) => Promise<AxiosResponse | AxiosError>;
  deleteTemplateType: (id: string) => Promise<AxiosResponse | AxiosError>;
  fetchTemplateType: (
    page: number,
    order: string,
    orderBy: string,
    search?: string,
  ) => Promise<void>;
}

export const TemplateCivilTypeContext = createContext<ITemplateCivilTypeContext>({} as ITemplateCivilTypeContext);

export const TemplateCivilTypesStorage = ({ children }: IProps) => {
  const [total, setTotal] = useState(0);
  const [templateTypes, setTemplateTypes] = useState<ITemplateCivilType[]>([]);
  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isNewModalVisible, setINewModalVisible] = useState(false);
  const { token } = useLogged();

  const requestOptions = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  async function handleModalVisible(visible: boolean) {
    setINewModalVisible(visible);
  }

  async function fetchTemplateType(
    page: number,
    order: string,
    orderBy: string,
    search?: string,
  ) {
    setLoading(true);
    setError(null);

    let url = `/templateCivilTypes/list?page=${page}&items=12&columnOrder=${orderBy}&order=${order}`;

    if (search) {
      url = `${url}&search=${search}`;
    }

    api
      .get(url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        setTemplateTypes(response.data.data.templateTypes);
        setTotal(response.data.data.templatesNumber);
        setTotalPages(response.data.data.totalPages);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  async function deleteTemplateType(id: string): Promise<AxiosResponse | AxiosError> {
    let response: AxiosResponse | AxiosError;

    try {
      setLoading(true);
      setError(null);

      response = await api.delete(`/templateCivilTypes/${id}`, requestOptions);

      await fetchTemplateType(1, 'asc', 'title');
    } catch (err) {
      response = err as AxiosError;
      setError(err);
    } finally {
      setLoading(false);
    }

    return response;
  }

  async function updateTemplateType(
    id: any,
    { title, description }: ITemplateCivilType
  ): Promise<AxiosResponse | AxiosError> {
    let response: AxiosResponse | AxiosError;

    setLoading(true);
    setError(null);

    try {
      setLoading(true);
      setError(null);

      const body = {
        title,
        description,
      };

      response = await api.put(
        `/templateCivilTypes/${id}`,
        body,
        requestOptions
      );

      await fetchTemplateType(1, 'asc', 'title')
    } catch (err) {
      response = err as AxiosError;
      setError(err);
    } finally {
      setLoading(false);
    }
    return response;
  }

  async function createTemplateType({
    title,
    description,
  }: ITemplateCivilType): Promise<AxiosResponse | AxiosError> {
    let response: AxiosResponse | AxiosError;

    try {
      setLoading(true);
      setError(null);

      const body = {
        title,
        description,
      };

      response = await api.post(
        "/templateCivilTypes",
        body,
        requestOptions
      );

      await fetchTemplateType(1, "asc", "title");
    } catch (err) {
      response = err as AxiosError;
      setError(err);
    } finally {
      setLoading(false);
    }

    return response;
  }

  useEffect(() => {
    fetchTemplateType(1, "asc", "title");
  }, []);

  return (
    <TemplateCivilTypeContext.Provider
      value={{
        fetchTemplateType,
        createTemplateType,
        updateTemplateType,
        deleteTemplateType,
        handleModalVisible,
        isNewModalVisible,
        templateTypes,
        totalPages,
        total,
        error,
        loading,
      }}
    >
      {children}
    </TemplateCivilTypeContext.Provider>
  );
};

export function useTemplateCivilTypes() {
  const context = useContext(TemplateCivilTypeContext);
  return context;
}
